import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { pushTrackingEvent } from '../utils/tracking';
import { goToDemoForm } from '../utils/demoNav';

const WHATSAPP_NUMBER = '919028912635';
const DISMISS_KEY = 'acolead_assistant_dismissed';

// What the assistant asks about, by page. First matching prefix wins.
const topics = [
  ['/features/lead-capture', 'capturing leads from every source', 'How do I get my portal and ad leads into AcoLead?'],
  ['/features/lead-routing', 'assigning leads to your team', 'Can AcoLead assign leads by project or area?'],
  ['/features/pipeline', 'setting up your sales pipeline', 'Can I set up my own pipeline stages?'],
  ['/features/sales-intelligence', 'finding your hottest leads', 'How does AcoLead tell me who to call first?'],
  ['/features/inventory', 'managing projects and units', 'How do I share matching properties with leads?'],
  ['/features/site-visits', 'site visits and reminders', 'How do site visit reminders work?'],
  ['/features/post-sales', 'payments after booking', 'How do payment demands and collections work?'],
  ['/features/resale', 'resale listings', 'How does the resale flow work?'],
  ['/features/whatsapp', 'WhatsApp automation', 'How do I connect my WhatsApp Business number?'],
  ['/features/instagram', 'Instagram leads', 'How do Instagram comments become leads?'],
  ['/features/facebook', 'Facebook leads', 'How do Facebook Lead Ads come into AcoLead?'],
  ['/features/calling', 'calling and recording', 'How are calls recorded on the lead?'],
  ['/features/inbox', 'the unified inbox', 'Can my team reply to every chat from one place?'],
  ['/features/marketing', 'marketing and campaigns', 'How do I see cost per site visit for my campaigns?'],
  ['/features/organic-leads', 'getting leads without ad spend', 'How do WhatsApp broadcasts and posts bring leads?'],
  ['/features/website', 'your website', 'Can AcoLead build a website on my own domain?'],
  ['/features/team-field', 'team attendance and field tracking', 'How does attendance with location work?'],
  ['/features/reports', 'reports', 'Can reports be sent to me on WhatsApp?'],
  ['/features/channel-partners', 'managing channel partners', 'How do channel partners tag clients?'],
  ['/features/partner-leads', 'sharing leads with partners', 'How does lead sharing with partners work?'],
  ['/features/cp-network', 'your CP network', 'How do I give my CPs their own CRM?'],
  ['/features/invoice-board', 'brokerage invoices', 'How do I bill builders for brokerage?'],
  ['/ai/sales-agent', 'the AI Sales Agent', 'Can the AI Sales Agent reply to my leads at night?'],
  ['/ai/connector', 'the AI Connector', 'How do I connect AcoLead to ChatGPT or Claude?'],
  ['/ai/nova', 'AcoLead Nova', 'When can I try AcoLead Nova?'],
  ['/zero-lead-breach', 'Zero Lead Breach', 'How does Zero Lead Breach protect my leads?'],
  ['/solutions/builders', 'AcoLead for builders', 'How would AcoLead work for my projects?'],
  ['/solutions/channel-partners', 'AcoLead for channel partners', 'How would AcoLead work for my agency?'],
  ['/solutions/mandate-companies', 'AcoLead for mandate firms', 'How would AcoLead work for my mandates?'],
  ['/solutions/resale', 'AcoLead for brokers', 'How would AcoLead work for resale?'],
  ['/pricing', 'pricing for your team', 'What would AcoLead cost for my team?'],
  ['/customers', 'how other teams use AcoLead', 'Which teams like mine use AcoLead?'],
];

const topicFor = (path) => {
  const match = topics.find(([prefix]) => path.startsWith(prefix));
  return match ? { topic: match[1], question: match[2] } : { topic: 'AcoLead', question: 'I’d like to know how AcoLead can help my sales team.' };
};

const readDismissed = () => {
  try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};
const saveDismissed = () => {
  try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* storage blocked */ }
};

const SiteFloatingActions = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { topic, question } = topicFor(pathname);
  const waText = `Hi AcoLead, ${question} (from ${pathname === '/' ? 'home page' : pathname})`;
  const waHref = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(waText)}`;

  useEffect(() => {
    setOpen(false);
    if (readDismissed() || pathname === '/demo' || pathname.startsWith('/demo/')) return undefined;
    const timer = setTimeout(() => {
      setOpen(true);
      pushTrackingEvent('assistant_shown', { page_path: pathname });
    }, 9000);
    return () => clearTimeout(timer);
  }, [pathname]);

  const dismiss = () => {
    setOpen(false);
    saveDismissed();
    pushTrackingEvent('assistant_dismissed', { page_path: pathname });
  };

  return (
    <Box
      component="aside"
      aria-label="Contact AcoLead"
      sx={{
        position: 'fixed',
        zIndex: 1300,
        left: { xs: 0, md: 'auto' },
        right: { xs: 0, md: 20 },
        bottom: { xs: 0, md: 20 },
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        p: 1.25,
        bgcolor: '#FFFFFF',
        borderTop: '1px solid #E1F0FD',
        '@media (min-width:1024px)': {
          left: 'auto',
          width: 'auto',
          p: 0,
          bgcolor: 'transparent',
          borderTop: 0,
        },
      }}
    >
      {open && (
        <Box
          role="dialog"
          aria-label="AcoLead assistant"
          sx={{
            position: 'absolute',
            right: { xs: 12, md: 0 },
            left: { xs: 12, sm: 'auto' },
            bottom: { xs: 'calc(100% + 10px)', md: 72 },
            width: { sm: 320 },
            p: 2,
            borderRadius: 3,
            bgcolor: '#FFFFFF',
            border: '1px solid #DCE6F2',
            boxShadow: '0 18px 48px rgba(12,35,68,.18)',
            animation: 'acoleadAssistantIn .28s ease-out',
            '@keyframes acoleadAssistantIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          <IconButton aria-label="Close assistant" onClick={dismiss} size="small" sx={{ position: 'absolute', top: 6, right: 6, color: '#5B6B80' }}><CloseRoundedIcon fontSize="small" /></IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pr: 3 }}>
            <Box aria-hidden="true" sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#0859C1', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 13 }}>A</Box>
            <Box>
              <Typography sx={{ color: '#14243A', fontSize: 13.5, fontWeight: 800, lineHeight: 1.2 }}>AcoLead Assistant</Typography>
              <Typography sx={{ color: '#3B9D1D', fontSize: 11, fontWeight: 700 }}>Replies on WhatsApp</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: '#14243A', fontSize: 14, lineHeight: 1.5, mb: 1.4 }}>
            Hi! Looking into <b>{topic}</b>? Ask us anything, or see it on your own leads.
          </Typography>
          <Box
            component="a"
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => pushTrackingEvent('assistant_whatsapp_click', { page_path: pathname })}
            sx={{ display: 'block', px: 1.4, py: 1, mb: 1, borderRadius: 2, bgcolor: '#F4F8FD', border: '1px solid #DCE6F2', color: '#0859C1', fontSize: 13, fontWeight: 700, textDecoration: 'none', '&:hover': { bgcolor: '#EAF2FC' } }}
          >
            “{question}”
          </Box>
          <Box sx={{ display: 'grid', gap: 0.8 }}>
            <Button
              component="a"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => pushTrackingEvent('assistant_whatsapp_click', { page_path: pathname })}
              startIcon={<WhatsAppIcon />}
              fullWidth
              sx={{ height: 42, whiteSpace: 'nowrap', bgcolor: '#25D366', color: '#fff', textTransform: 'none', fontSize: 14, fontWeight: 800, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#1EBA59' } }}
            >
              Ask on WhatsApp
            </Button>
            <Button
              component={RouterLink}
              to="/demo"
              onClick={(event) => { setOpen(false); goToDemoForm(event, 'assistant'); }}
              variant="outlined"
              fullWidth
              sx={{ height: 42, whiteSpace: 'nowrap', color: '#0859C1', borderColor: '#B9CBE0', textTransform: 'none', fontSize: 14, fontWeight: 800, borderRadius: 2, '&:hover': { borderColor: '#0859C1', bgcolor: '#F8FBFF' } }}
            >
              Book a Demo
            </Button>
          </Box>
        </Box>
      )}
      <Button
        component={RouterLink}
        to="/demo"
        onClick={(event) => goToDemoForm(event, 'floating')}
        variant="contained"
        color="primary"
        sx={{
          display: 'inline-flex',
          flex: 1,
          minHeight: 46,
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          '@media (min-width:1024px)': { display: 'none' },
        }}
      >
        Book a Demo
      </Button>
      <Tooltip title="Chat with AcoLead on WhatsApp">
        <IconButton
          component="a"
          href={waHref}
          onClick={() => pushTrackingEvent('whatsapp_click', { page_path: pathname })}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat with AcoLead on WhatsApp"
          sx={{
            width: 46,
            height: 46,
            color: '#FFFFFF',
            bgcolor: '#25D366',
            boxShadow: '0 8px 24px rgba(37, 211, 102, 0.32)',
            '&:hover': { bgcolor: '#1EBA59' },
            '@media (min-width:1024px)': { width: 58, height: 58 },
          }}
        >
          <WhatsAppIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default SiteFloatingActions;
